import { Controller } from "@hotwired/stimulus"

// Google-Calendar-style time picker. A text input + a dropdown of
// 15-minute increments. The canonical 24h HH:MM lives in a hidden
// field (so the server combine logic is unchanged); the visible input
// shows a friendly 12h label ("2:00pm"). Supports click-to-open,
// type-to-filter, and free-text parse on blur/Enter.
//
// Emits a `time-select:change` custom event (bubbling) whenever the
// value changes via user action — the meeting-duration controller
// listens for it to keep Start / Duration / End in sync. Programmatic
// updates via `setValue()` do NOT emit, so callers stay loop-free.
const STEP_MIN = 15
const DAY_MIN = 24 * 60

export default class extends Controller {
  static targets = ["input", "hidden", "menu"]

  connect() {
    this._built = false
    this._syncInputFromHidden()
    this._onDocClick = (e) => {
      if (!this.element.contains(e.target)) this.close()
    }
    document.addEventListener("click", this._onDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick)
  }

  // Public: canonical 24h "HH:MM" (or "" when empty).
  get value() {
    return this.hiddenTarget.value || ""
  }

  // Public: set value programmatically (no event emitted).
  setValue(hhmm) {
    this.hiddenTarget.value = hhmm || ""
    this._syncInputFromHidden()
  }

  open() {
    this._buildOptions()
    this.menuTarget.classList.remove("hidden")
    this._filterOptions("")
    this._scrollToSelected()
  }

  close() {
    this.menuTarget.classList.add("hidden")
  }

  // Live type-to-filter.
  filter() {
    this._buildOptions()
    this.menuTarget.classList.remove("hidden")
    this._filterOptions(this.inputTarget.value)
  }

  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      const firstVisible = this._visibleOptions()[0]
      if (firstVisible) {
        this._commit(firstVisible.dataset.value)
      } else {
        this._commitFreeText(this.inputTarget.value)
      }
      this.close()
    } else if (event.key === "Escape") {
      this.close()
    }
  }

  // Click on a dropdown option.
  pick(event) {
    this._commit(event.currentTarget.dataset.value)
    this.close()
  }

  // ─── private ──────────────────────────────────────────────────

  _commit(hhmm) {
    this.hiddenTarget.value = hhmm
    this._syncInputFromHidden()
    this._emitChange()
  }

  _commitFreeText(text) {
    const parsed = this._parse(text)
    if (parsed != null) {
      this.hiddenTarget.value = parsed
      this._syncInputFromHidden()
      this._emitChange()
    } else {
      // revert display to last good value
      this._syncInputFromHidden()
    }
  }

  _emitChange() {
    this.element.dispatchEvent(
      new CustomEvent("time-select:change", { bubbles: true })
    )
  }

  _syncInputFromHidden() {
    const v = this.hiddenTarget.value
    this.inputTarget.value = v ? this._to12(v) : ""
  }

  _buildOptions() {
    if (this._built) return
    const frag = document.createDocumentFragment()
    for (let m = 0; m < DAY_MIN; m += STEP_MIN) {
      const hhmm = this._toHHMM(m)
      const li = document.createElement("li")
      li.dataset.value = hhmm
      li.dataset.label = this._to12(hhmm).toLowerCase()
      li.textContent = this._to12(hhmm)
      li.className =
        "px-3 py-1.5 cursor-pointer hover:bg-gray-50 text-gray-700"
      li.setAttribute("data-action", "click->time-select#pick")
      frag.appendChild(li)
    }
    this.menuTarget.appendChild(frag)
    this._built = true
  }

  _filterOptions(query) {
    const q = query.trim().toLowerCase().replace(/\s+/g, "")
    this.menuTarget.querySelectorAll("li").forEach((li) => {
      const label = li.dataset.label.replace(/\s+/g, "")
      li.classList.toggle("hidden", q.length > 0 && !label.startsWith(q) && !label.includes(q))
    })
  }

  _visibleOptions() {
    return Array.from(this.menuTarget.querySelectorAll("li")).filter(
      (li) => !li.classList.contains("hidden")
    )
  }

  _scrollToSelected() {
    const v = this.hiddenTarget.value
    if (!v) return
    const sel = this.menuTarget.querySelector(`li[data-value="${v}"]`)
    if (sel) this.menuTarget.scrollTop = sel.offsetTop - this.menuTarget.offsetTop - 40
  }

  // "14:30" → "2:30pm"
  _to12(hhmm) {
    const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10))
    const period = h >= 12 ? "pm" : "am"
    let h12 = h % 12
    if (h12 === 0) h12 = 12
    return `${h12}:${String(m).padStart(2, "0")}${period}`
  }

  _toHHMM(min) {
    const h = Math.floor(min / 60)
    const m = min % 60
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }

  // Parse free text like "2:30pm", "230pm", "14:30", "2pm" → "HH:MM".
  _parse(text) {
    if (!text) return null
    const t = text.trim().toLowerCase().replace(/\s+/g, "")
    const m = t.match(/^(\d{1,2})(?::?(\d{2}))?(am|pm)?$/)
    if (!m) return null
    let hour = parseInt(m[1], 10)
    const min = m[2] ? parseInt(m[2], 10) : 0
    const period = m[3]
    if (min > 59) return null
    if (period === "pm" && hour < 12) hour += 12
    if (period === "am" && hour === 12) hour = 0
    if (hour > 23) return null
    return `${String(hour).padStart(2, "0")}:${String(min).padStart(2, "0")}`
  }
}
